import {
  Button,
  Card,
  CardContent,
  PaginationComponent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  brokerageFirmControllerListReportsQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDate, formatDateWithTime } from '../../../lib/date-formatting';
import { RefreshCcw } from 'lucide-react';
import { Group } from '@folio/components/ui/flex';

interface ReportsForBrokerageFirmListProps {
  reportType: string;
}

// EDI report names can end in a timestamp rather than an extension (CFDP MSG002
// mandates no extension), which would label the button "1430".
function fileExtension(name: string): string | undefined {
  if (!name.includes('.')) {
    return undefined;
  }

  const candidate = name.split('.').at(-1);

  return candidate && /^(?=.*[a-z])[a-z0-9]{1,5}$/i.test(candidate) ? candidate : undefined;
}

export function ReportsForBrokerageFirmList({ reportType }: ReportsForBrokerageFirmListProps) {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const currentPage = useMemo(() => {
    const page = Number.parseInt(searchParams.get('page') ?? '', 10);
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams]);

  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user.brokerageFirmId!),
    enabled: !!user.brokerageFirmId,
  });

  const productIds = [
    ...(brokerageFirm?.activeProducts ?? []),
    ...(brokerageFirm?.inactiveProducts ?? []),
  ];

  const {
    data: reportings,
    status,
    isLoading,
    refetch,
  } = useQuery({
    ...brokerageFirmControllerListReportsQueryOptions(user?.brokerageFirmId!, {
      count: 10,
      page: currentPage,
      reportTypes: reportType ? [reportType] : undefined,
    }),
  });

  return (
    <Card>
      <CardContent className="py-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.list.columns.name')}</TableHead>
                {productIds.length > 1 && (
                  <TableHead>{t('reports.list.columns.products')}</TableHead>
                )}
                <TableHead>{t('reports.list.columns.period')}</TableHead>
                <TableHead>{t('reports.list.columns.createdAt')}</TableHead>
                <TableHead>{t('reports.list.columns.download')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportType && status === 'pending' ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: is ok
                Array.from({ length: 10 }).map((_, idx) => <LoadingRow key={idx} />)
              ) : status === 'error' ? (
                <ErrorRow />
              ) : !reportings || reportings.data.length === 0 ? (
                <ErrorRow>{t('reports.list.empty')}</ErrorRow>
              ) : (
                reportings.data.map(report => {
                  const reportFrom =
                    report.metadata.find(({ key }) => key === 'createdfrom')?.value ??
                    report.metadata.find(({ key }) => key === 'from')?.value;

                  const reportTo =
                    report.metadata.find(({ key }) => key === 'createdto')?.value ??
                    report.metadata.find(({ key }) => key === 'to')?.value;

                  return (
                    <TableRow key={report.id}>
                      <TableCell className="max-w-xs">
                        <span className="w-fulltext-sm break-words whitespace-pre-wrap">
                          {report.name}
                        </span>
                      </TableCell>
                      {productIds.length > 1 && (
                        <TableCell>
                          {report.metadata
                            .find(({ key }) => key === 'productids')
                            ?.value.split(',')
                            .map(id => id.trim())
                            .map(id => t(`products.${id}.label`))
                            .join(', ') ??
                            report.metadata
                              .filter(({ key }) => key === 'productid')
                              .map(({ value }) => t(`products.${value}.label`))
                              .join(', ')}
                        </TableCell>
                      )}
                      <TableCell>
                        {reportFrom && reportTo
                          ? `${formatDate(new Date(reportFrom))} -> ${formatDate(new Date(reportTo))}`
                          : reportFrom
                            ? t('reports.list.periodFrom', {
                                date: formatDate(new Date(reportFrom)),
                              })
                            : reportTo
                              ? t('reports.list.periodTo', {
                                  date: formatDate(new Date(reportTo)),
                                })
                              : ''}
                      </TableCell>
                      <TableCell>{formatDateWithTime(new Date(report.createdAt))}</TableCell>
                      <TableCell>
                        <Link to={report.signedUrl}>
                          <Button variant="outline" size="sm">
                            {fileExtension(report.name) ?? t('reports.list.columns.download')}
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <Group align="stretch" justify="center" gap={0} className="mt-4">
          <PaginationComponent
            totalData={reportings?.totalCount ?? 0}
            PAGE_SIZE={10}
            isLoading={isLoading}
          />
          <Button
            variant="outline"
            leftIcon={<RefreshCcw className="h-4 w-4" />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {t('common.refresh')}
          </Button>
        </Group>
      </CardContent>
    </Card>
  );
}
